import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProductionDeclarationMaterialAdditionalFilesDeleteDialog } from '@/features/production-declaration/components/statement/production-declaration-material-additional-files-delete-dialog';
import { ProductionDeclarationMaterialAdditionalFilesDownloadButton } from '@/features/production-declaration/components/statement/production-declaration-material-additional-files-download-button';
import { useCanEditProductionDeclaration } from '@/features/production-declaration/hooks/use-can-edit-production-declaration';
import { useProductionDeclarationMaterialAdditionalFilesDeleteDialog } from '@/features/production-declaration/hooks/use-production-declaration-material-additional-files-delete-dialog';
import type { ProductionDeclarationCommodityOriginDocument } from '@/types/core/purchase-order/production-declaration-commodity-origin-document';
import type { ProductionDeclarationStatusCode as ProductionDeclarationStatusCodeType } from '@/types/core/purchase-order/production-declaration-status-code';

const ADDITIONAL_FILES_SKELETON_ROW_KEYS = [
  'additional-file-skeleton-1',
  'additional-file-skeleton-2',
  'additional-file-skeleton-3',
] as const;

type ProductionDeclarationMaterialAdditionalFilesListProps = {
  commodityOriginId: string;
  documents: ProductionDeclarationCommodityOriginDocument[];
  isLoading: boolean;
  materialId: string;
  productionDeclarationId: string;
  statusCode: ProductionDeclarationStatusCodeType;
  token?: string;
};

function ProductionDeclarationMaterialAdditionalFilesListHeader({
  showActions,
}: {
  showActions: boolean;
}) {
  const { t } = useTranslation('productionDeclaration');

  return (
    <TableHeader>
      <TableRow className='border-declaration-muted hover:bg-transparent'>
        <TableHead className='px-4 py-3 align-middle font-medium text-base text-declaration-muted-foreground'>
          {t('materialCommodityOrigins.cards.additionalFiles.columns.name')}
        </TableHead>
        {showActions ? (
          <TableHead className='px-4 py-3 text-right align-middle font-medium text-base text-declaration-muted-foreground'>
            {t(
              'materialCommodityOrigins.cards.additionalFiles.columns.actions',
            )}
          </TableHead>
        ) : null}
      </TableRow>
    </TableHeader>
  );
}

function ProductionDeclarationMaterialAdditionalFilesListLoadingState({
  showActions,
}: {
  showActions: boolean;
}) {
  return (
    <Table className='w-full table-fixed'>
      <ProductionDeclarationMaterialAdditionalFilesListHeader
        showActions={showActions}
      />
      <TableBody>
        {ADDITIONAL_FILES_SKELETON_ROW_KEYS.map((rowKey) => (
          <TableRow
            className='border-declaration-border-light hover:bg-transparent'
            key={rowKey}
          >
            <TableCell className='px-4 py-4'>
              <Skeleton className='h-8 w-48 rounded-full bg-declaration-skeleton' />
            </TableCell>
            {showActions ? (
              <TableCell className='px-4 py-4'>
                <div className='flex justify-end'>
                  <Skeleton className='h-8 w-8 rounded-full bg-declaration-skeleton' />
                </div>
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ProductionDeclarationMaterialAdditionalFilesList({
  commodityOriginId,
  documents,
  isLoading,
  materialId,
  productionDeclarationId,
  statusCode,
  token,
}: ProductionDeclarationMaterialAdditionalFilesListProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const canDelete = useCanEditProductionDeclaration(statusCode);
  const showActions =
    canDelete || documents.some((document) => document.clientUrl);
  const {
    handleDelete,
    handleDeleteDialogOpenChange,
    isDeleteDialogOpen,
    isPending,
    openDeleteDialog,
  } = useProductionDeclarationMaterialAdditionalFilesDeleteDialog({
    commodityOriginId,
    materialId,
    productionDeclarationId,
    token,
  });

  if (isLoading) {
    return (
      <ProductionDeclarationMaterialAdditionalFilesListLoadingState
        showActions={showActions}
      />
    );
  }

  if (documents.length === 0) {
    return null;
  }

  return (
    <>
      <div className='overflow-x-auto'>
        <Table className='w-full table-fixed'>
          <ProductionDeclarationMaterialAdditionalFilesListHeader
            showActions={showActions}
          />
          <TableBody>
            {documents.map((document) => (
              <TableRow
                className='border-declaration-border-light hover:bg-transparent'
                key={document.id}
              >
                <TableCell className='max-w-0 px-4 py-3.5'>
                  {document.clientUrl ? (
                    <a
                      className='block truncate font-medium text-declaration-foreground underline underline-offset-2 transition-colors hover:text-declaration-foreground-muted'
                      href={document.clientUrl}
                      rel='noopener noreferrer'
                      target='_blank'
                      title={document.name}
                    >
                      {document.name}
                    </a>
                  ) : (
                    <div
                      className='truncate font-medium text-declaration-foreground'
                      title={document.name}
                    >
                      {document.name}
                    </div>
                  )}
                </TableCell>
                <TableCell className='px-4 py-3.5'>
                  <div className='flex justify-end gap-3'>
                    <ProductionDeclarationMaterialAdditionalFilesDownloadButton
                      document={document}
                    />
                    {canDelete ? (
                      <Button
                        aria-label={t(
                          'materialCommodityOrigins.cards.additionalFiles.actions.delete',
                        )}
                        className='h-auto p-0 text-declaration-foreground shadow-none transition-colors hover:bg-transparent hover:text-declaration-foreground-muted dark:text-declaration-foreground dark:hover:bg-transparent dark:hover:text-declaration-foreground-muted'
                        disabled={isPending}
                        onClick={() => {
                          openDeleteDialog(document);
                        }}
                        type='button'
                        variant='ghost'
                      >
                        <Trash2 className='size-[1.2rem]' strokeWidth={2.1} />
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ProductionDeclarationMaterialAdditionalFilesDeleteDialog
        isPending={isPending}
        onConfirm={handleDelete}
        onOpenChange={handleDeleteDialogOpenChange}
        open={isDeleteDialogOpen}
      />
    </>
  );
}
